'use client';
import { useState } from 'react';
import Link from 'next/link';
import { ShareBar } from '@/components/shared/ShareBar';
import { trackLead } from '@/lib/analytics';

function EmailCapture({ category, label }: { category: string; label: string }) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState(false);
  // Fangst-broen: poster til smakebit-magneten (lead_downloads + full drip)
  // i stedet for døde newsletter_signups. utm_source gjør flaten målbar.
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !consent) return;
    setError(false);
    try {
      const res = await fetch('/api/smakebit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, consent: true, utm: { source: `kalkulator:${category}` } }),
      });
      if (!res.ok) { setError(true); return; }
    } catch { setError(true); return; }
    trackLead('generate_lead', { content_name: category, method: 'kalkulator' });
    setDone(true);
  }
  if (done) return (
    <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center mb-6">
      <div className="text-green-800 font-medium mb-1">Ukesmenyen er på vei!</div>
      <div className="text-green-600 text-sm">Sjekk innboksen — kanskje også søppelposten første gang. Du får også tips om {label.toLowerCase()} fremover.</div>
    </div>
  );
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-2xl p-5 mb-6">
      <h3 className="font-semibold text-stone-900 mb-1">Få 7 middager under 500 kcal — gratis</h3>
      <p className="text-stone-500 text-sm mb-4">Ferdig ukesmeny som PDF med handleliste og næringsinnhold — pluss evidensbaserte tips om {label.toLowerCase()} (maks 1–2 e-poster i uka).</p>
      {error && <p className="text-red-600 text-xs mb-3">Noe gikk galt — prøv igjen om et øyeblikk.</p>}
      <form onSubmit={submit} className="space-y-3">
        <input type="email" placeholder="din@epost.no" value={email} onChange={e => setEmail(e.target.value)}
          className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-stone-400" />
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5 flex-shrink-0" />
          <span className="text-xs text-stone-500">Jeg samtykker til å motta markedsføring fra CoachGuiden per e-post. Du kan melde deg av når som helst.</span>
        </label>
        <button type="submit" disabled={!email || !consent} className="w-full py-2.5 bg-stone-900 text-white rounded-xl text-sm font-medium hover:bg-stone-800 disabled:opacity-40 transition-colors">
          Meld meg på gratis tips
        </button>
      </form>
    </div>
  );
}


export default function Kalorikalkulator() {
  const [height, setHeight] = useState(175);
  const [weight, setWeight] = useState(75);
  const [age, setAge] = useState(30);
  const [gender, setGender] = useState('mann');
  const [activity, setActivity] = useState(1.55);
  const [goal, setGoal] = useState('vedlikehold');

  // Mifflin-St Jeor
  const bmr = gender === 'mann'
    ? Math.round(10 * weight + 6.25 * height - 5 * age + 5)
    : Math.round(10 * weight + 6.25 * height - 5 * age - 161);
  const tdee = Math.round(bmr * activity);

  const targets = {
    raskt_ned: { kcal: tdee - 1000, label: 'Raskt ned (−1 kg/uke)', color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
    moderat_ned: { kcal: tdee - 500, label: 'Moderat ned (−0.5 kg/uke)', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
    vedlikehold: { kcal: tdee, label: 'Vedlikehold', color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
    moderat_opp: { kcal: tdee + 300, label: 'Muskelvekst (moderate)', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
    raskt_opp: { kcal: tdee + 600, label: 'Muskelvekst (aggressiv)', color: 'text-violet-600', bg: 'bg-violet-50 border-violet-200' },
  };

  const current = targets[goal];
  const protein = Math.round(weight * 2);
  const fat = Math.round((current.kcal * 0.25) / 9);
  const carbs = Math.round((current.kcal - protein * 4 - fat * 9) / 4);

  const activityLevels = [
    [1.2, 'Stillesittende', 'Ingen/lite trening'],
    [1.375, 'Lett aktiv', '1–3 dager/uke'],
    [1.55, 'Moderat aktiv', '3–5 dager/uke'],
    [1.725, 'Meget aktiv', '6–7 dager/uke'],
    [1.9, 'Ekstremt aktiv', 'Trening 2x/dag'],
  ];

  return (
    <div className="min-h-screen gradient-warm">
      <header className="glass border-b border-stone-200/50 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 font-semibold text-stone-900">
            <span className="w-7 h-7 rounded-lg bg-stone-900 flex items-center justify-center"><svg viewBox="0 0 32 32" fill="none" className="w-5 h-5"><circle cx="16" cy="16" r="11" stroke="#44403c" strokeWidth="1"/><circle cx="16" cy="16" r="7.5" stroke="#57534e" strokeWidth="0.7"/><polygon points="16,6 17.5,13 16,12 14.5,13" fill="#ffffff"/><polygon points="16,26 14.5,19 16,20 17.5,19" fill="#78716c"/><path d="M16,5 A11,11 0 1,0 16,27" stroke="#f5f5f4" strokeWidth="1.8" strokeLinecap="round" fill="none"/></svg></span>
            CoachGuiden
          </Link>
          <Link href="/ressurser" className="text-sm text-stone-500 hover:text-stone-900">← Alle kalkulatorer</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <nav className="text-xs text-stone-400 mb-6 flex items-center gap-1">
          <Link href="/" className="hover:text-stone-600">Hjem</Link><span>›</span>
          <Link href="/ressurser" className="hover:text-stone-600">Ressurser</Link><span>›</span>
          <span className="text-stone-600">Kalorikalkulator</span>
        </nav>

        <h1 className="text-3xl font-semibold text-stone-900 mb-2">Kalorikalkulator</h1>
        <p className="text-stone-500 mb-6">Finn ut nøyaktig hvor mange kalorier du trenger per dag basert på ditt mål.</p>
        <div className="rounded-3xl overflow-hidden shadow-xl shadow-stone-200/60 aspect-[16/9] mb-8">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/images/tools/kalorikalkulator.jpg" alt="Kalorikalkulator" className="w-full h-full object-cover" loading="eager"/>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          {/* Inputs */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                ['Alder', age, setAge, 18, 90, 'år'],
                ['Vekt', weight, setWeight, 40, 200, 'kg'],
                ['Høyde', height, setHeight, 140, 220, 'cm'],
              ].map(([l,v,s,min,max,unit]: any) => (
                <div key={l} className={l==='Høyde' ? 'col-span-2' : ''}>
                  <label className="text-xs text-stone-500 block mb-1">{l}</label>
                  <div className="flex items-center gap-2">
                    <input type="number" value={v} min={min} max={max} onChange={e=>(s)(+e.target.value)}
                      className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-stone-400"/>
                    <span className="text-xs text-stone-400">{unit}</span>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <label className="text-xs text-stone-500 block mb-2">Kjønn</label>
              <div className="flex gap-2">
                {[['mann','Mann'],['kvinne','Kvinne']].map(([v,l]: any) => (
                  <button key={v} onClick={() => setGender(v)} className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${gender===v?'bg-stone-900 text-white':'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-stone-500 block mb-2">Aktivitetsnivå</label>
              <div className="space-y-1">
                {activityLevels.map(([val, label, sub]: any) => (
                  <button key={val} onClick={() => setActivity(val)} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs transition-colors text-left ${activity===val?'bg-stone-900 text-white':'bg-stone-50 text-stone-600 hover:bg-stone-100'}`}>
                    <span className="font-medium">{label}</span>
                    <span className={activity===val?'text-stone-400':'text-stone-400'}>{sub}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-3">
            <div className="bg-white border border-stone-200 rounded-2xl p-5">
              <div className="text-xs text-stone-400 mb-3">Grunnmetabolisme (BMR)</div>
              <div className="text-3xl font-semibold text-stone-900">{bmr.toLocaleString('no')}</div>
              <div className="text-xs text-stone-400">kcal/dag i fullstendig hvile</div>
              <div className="mt-3 pt-3 border-t border-stone-100">
                <div className="text-xs text-stone-400 mb-1">Totalt energibehov (TDEE)</div>
                <div className="text-2xl font-semibold text-stone-900">{tdee.toLocaleString('no')}</div>
                <div className="text-xs text-stone-400">kcal/dag ved ditt aktivitetsnivå</div>
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-stone-500 mb-2">Velg mål</div>
              <div className="space-y-1.5">
                {Object.entries(targets).map(([k, v]) => (
                  <button key={k} onClick={() => setGoal(k)}
                    className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm border transition-all ${goal===k?'border-stone-900 bg-stone-900 text-white':'border-stone-200 bg-white text-stone-700 hover:bg-stone-50'}`}>
                    <span>{v.label}</span>
                    <span className="font-semibold">{v.kcal.toLocaleString('no')} kcal</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Macros */}
        <div className={`border rounded-2xl p-6 mb-6 ${current.bg}`}>
          <h2 className="font-semibold text-stone-900 mb-4">Anbefalt makrofordeling for <span className={current.color}>{current.label.toLowerCase()}</span></h2>
          <div className="grid grid-cols-3 gap-4">
            {[['Protein', protein, 'g', `${Math.round(protein*4)} kcal`,'bg-blue-500'],['Fett', fat, 'g', `${Math.round(fat*9)} kcal`,'bg-amber-500'],['Karbohydrat', carbs, 'g', `${Math.round(carbs*4)} kcal`,'bg-green-500']].map(([l,v,u,k,col]) => (
              <div key={l} className="bg-white rounded-xl p-4 text-center border border-stone-100">
                <div className={`w-3 h-3 rounded-full ${col} mx-auto mb-2`}/>
                <div className="text-2xl font-semibold text-stone-900">{v}{u}</div>
                <div className="text-xs text-stone-500">{l}</div>
                <div className="text-xs text-stone-400">{k}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-stone-500 mt-4">Basert på Mifflin-St Jeor-formelen og 2g protein/kg kroppsvekt. Individuelle behov kan variere.</p>
        </div>

        {/* Source/explanation */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
          <h2 className="font-semibold text-stone-900 mb-4">Kilde og beregningsmetode</h2>
          <div className="space-y-3 text-sm text-stone-600">
            <div className="flex gap-3"><span className="text-stone-400 w-28 flex-shrink-0">Formel (BMR)</span><span>Mifflin-St Jeor: Mann: (10 × vekt) + (6,25 × høyde) − (5 × alder) + 5. Kvinne: samme minus 161.</span></div>
            <div className="flex gap-3"><span className="text-stone-400 w-28 flex-shrink-0">Aktivitetsfaktor</span><span>Standard aktivitetsmultiplikatorer: 1.2 (stillesittende) til 1.9 (ekstremt aktiv). Multipliseres med BMR for å gi TDEE.</span></div>
            <div className="flex gap-3"><span className="text-stone-400 w-28 flex-shrink-0">Kilde</span><span>Mifflin MD et al. (1990). A new predictive equation for resting energy expenditure. <em>American Journal of Clinical Nutrition</em>, 51(2):241–7. Validert som mest nøyaktig formel per 2025.</span></div>
            <div className="flex gap-3"><span className="text-stone-400 w-28 flex-shrink-0">Makro</span><span>Proteinen beregnet som 2g/kg kroppsvekt (normen for aktive). Fett 25% av kalorier. Resten karbohydrater.</span></div>
            <div className="bg-stone-50 rounded-xl p-3 text-xs text-stone-500">Kalkulatoren gir estimater. Faktisk behov varierer med søvn, stress, hormoner og genetikk. En ernæringsfysiolog kan gi individuell vurdering basert på blodprøver og kroppssammensetning.</div>
          </div>
        </div>

        {/* Internal links */}
        <div className="bg-stone-50 border border-stone-200 rounded-2xl p-5 mb-6">
          <h3 className="font-medium text-stone-900 mb-3">Relaterte ressurser</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { href: "/ressurser/bmi-kalkulator", title: "BMI-kalkulator", desc: "Beregn BMI og idealvekt", color: "bg-orange-50 border-orange-200 text-orange-700" },
              { href: "/kosthold", title: "Finn ernæringsfysiolog", desc: "Personlig kostholdsplan", color: "bg-green-50 border-green-200 text-green-700" },
              { href: "/trening", title: "Finn personlig trener", desc: "Trening tilpasset dine mål", color: "bg-orange-50 border-orange-200 text-orange-700" },
              { href: "/ressurser/artikler/ga-ned-i-vekt-uten-diett", title: "Gå ned i vekt uten å sulte", desc: "Les vår evidensbaserte guide", color: "bg-stone-100 border-stone-200 text-stone-700" },
            ].map(l => (
              <a key={l.href} href={l.href} className={"flex items-center gap-3 border rounded-xl p-3 hover:shadow-sm transition-all " + l.color}>
                <div><div className="text-sm font-medium">{l.title}</div><div className="text-xs opacity-70">{l.desc}</div></div>
                <span className="ml-auto text-xs">→</span>
              </a>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6 mb-10">
          <div>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">Hva er BMR og TDEE?</h2>
            <p className="text-stone-600 text-sm leading-relaxed">BMR (Basal Metabolic Rate) er energien kroppen forbruker i fullstendig hvile for å holde seg i live — puste, pumpe blod, opprettholde kroppstemperatur. TDEE (Total Daily Energy Expenditure) er det totale energibehovet inkludert all aktivitet. For å gå ned i vekt må du spise under TDEE. For å gå opp i vekt, over.</p>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">Hvor nøyaktig er kalkulatoren?</h2>
            <p className="text-stone-600 text-sm leading-relaxed">Mifflin-St Jeor er den mest validerte formelen per 2025, men alle kalkulatorer gir estimater. Faktorer som søvnkvalitet, stressnivå og muskelmasse påvirker metabolismen. En ernæringsfysiolog kan gi deg en individuell vurdering basert på blodprøver og kroppssammensetning.</p>
          </div>
        </div>

        <EmailCapture category="kosthold" label="Kosthold og ernæring" />
        <div className="bg-stone-900 text-white rounded-2xl p-8 text-center">
          <div className="text-stone-400 text-xs uppercase tracking-widest mb-3">Neste steg</div>
          <h2 className="font-bold text-2xl mb-2">Du vet hva kroppen trenger.</h2>
          <p className="text-stone-300 text-base mb-1">En ernæringsfysiolog hjelper deg å <span className="text-white font-semibold">spise deg til resultater</span> —</p>
          <p className="text-stone-400 text-sm mb-6">uten å telle kalorier manuelt resten av livet.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/kosthold" className="inline-block px-6 py-3 bg-white text-stone-900 rounded-xl text-sm font-semibold hover:bg-stone-100">Finn ernæringsfysiolog gratis →</Link>
            <Link href="/trening" className="inline-block px-6 py-3 bg-stone-700 text-white rounded-xl text-sm font-medium hover:bg-stone-600">Finn personlig trener →</Link>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-6 pb-12">
          <ShareBar label="Del dette verktøyet" />
        </div>
      </main>
    </div>
  );
}
